import { storageImage, storageSrcSet } from "@/lib/imageUrl";
import { Link } from "@/lib/router-compat";
import { useState } from "react";
import { useIndustries } from "@/hooks/useIndustries";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { ArrowRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { MarketFilter } from "@/components/MarketFilter";
import Autoplay from "embla-carousel-autoplay";
import { toSlug } from "@/lib/utils";
import { LOAD_ERROR_MESSAGE } from "@/lib/publicQueries";
const IndustryCard = ({ industry, to }: any) => {
  return (
    <Link
      to={to}
      className="group block cursor-pointer"
    >
      <div className="relative overflow-hidden rounded-xl bg-card border border-border transition-all duration-500 hover:border-primary/50 hover:shadow-xl hover:shadow-primary/20 hover:-translate-y-2">
        {/* Image */}
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
                          src={storageImage(industry.coverImage, 800)}
                          srcSet={storageSrcSet(industry.coverImage)}
                          sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
                          alt={industry.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                          decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
        </div>

        {/* Content */}
        <div className="p-6">
          <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors">
            {industry.name}
          </h3>
          <p className="text-muted-foreground text-sm mb-4">
            Professionelle Produktionen für {industry.name}
          </p>
          
          <div className="flex items-center gap-2 text-primary text-sm font-medium">
            <span>Projekte ansehen</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    </Link>
  );
};

const PortfolioSection = () => {
  const { data: industries = [], isLoading, isError } = useIndustries();
  const [selectedMarket, setSelectedMarket] = useState<'de' | 'en'>('de');

  const industryHref = (industryName: string) =>
    `/portfolio/industry/${toSlug(industryName)}?market=${selectedMarket}`;

  const handleMarketChange = (market: 'de' | 'en') => {
    setSelectedMarket(market);
  };

  return (
    <section id="portfolio" className="section-padding border-t border-border/50">
      <div className="container-custom">
        <div className="text-center mb-12">
          <Link to="/portfolio" className="group inline-flex items-center gap-3 mb-4 hover:opacity-80 transition-opacity">
            <h2 className="text-3xl md:text-5xl font-bold">
              Unser Portfolio
            </h2>
            <ArrowRight className="w-6 h-6 md:w-8 md:h-8 text-primary group-hover:translate-x-2 transition-transform" />
          </Link>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
            Wählen Sie eine Branche, um unsere Arbeiten zu entdecken. Jedes Projekt erzählt seine eigene Geschichte – mit Präzision und kreativem Anspruch umgesetzt.
          </p>

          {/* Market Filter */}
          <MarketFilter 
            selectedMarket={selectedMarket}
            onMarketChange={handleMarketChange}
          />
        </div>

        {isError && industries.length === 0 ? (
          <p role="alert" className="text-center text-muted-foreground py-12">{LOAD_ERROR_MESSAGE}</p>
        ) : isLoading ? (
          <div className="w-full max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-xl overflow-hidden">
                  <Skeleton className="aspect-[4/3] w-full" />
                  <div className="p-6 space-y-2">
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <Carousel
            opts={{
              align: "start",
              loop: true,
              dragFree: false,
              watchDrag: true,
            }}
            plugins={[
              Autoplay({
                delay: 3000,
                stopOnInteraction: false,
                stopOnMouseEnter: true,
              })
            ]}
            className="w-full max-w-6xl mx-auto"
          >
            <CarouselContent className="-ml-4">
              {industries.map((industry, index) => (
                <CarouselItem key={index} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <IndustryCard
                    industry={industry}
                    to={industryHref(industry.name)}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex" />
            <CarouselNext className="hidden md:flex" />
          </Carousel>
        )}
      </div>
    </section>
  );
};

export default PortfolioSection;
